import React, { useEffect, useState } from "react";
import checkIcon from "../../../assets/images/blue_tick.png";
import noDeliveryHistory from "../../../assets/images/no_delivery_history.png";
import HamburgerMenuHeader from "../../Header/DeliveryMenuHeader";
import { fetchSessionHistory } from "../../../services/delivery_api";
import cross from "../../../assets/images/cross.png";
import { useTranslation } from "react-i18next";

const DeliveryHistory = () => {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  // Filter UI states
  const [showFilterScreen, setShowFilterScreen] = useState(false);
  const [activeTab, setActiveTab] = useState("Locations");

  const [selectedLocation, setSelectedLocation] = useState("All");
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  const [initialized, setInitialized] = useState(false);

  // Location -> Buildings dict
  const [locationDict, setLocationDict] = useState({});

  const isFilterApplied =
    (selectedLocation && selectedLocation !== "All") ||
    (selectedBuilding && selectedBuilding !== "All");

  // ✅ Load saved filters from localStorage
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("deliveryHistoryFilters"));

    if (saved) {
      setSelectedLocation(saved.selectedLocation ?? "All");
      setSelectedBuilding(saved.selectedBuilding ?? null);
    }

    setInitialized(true);
  }, []);

  // ✅ Save filters after initialization
  useEffect(() => {
    if (!initialized) return;

    localStorage.setItem(
      "deliveryHistoryFilters",
      JSON.stringify({
        selectedLocation,
        selectedBuilding,
      }),
    );
  }, [initialized, selectedLocation, selectedBuilding]);

  // ✅ Fetch history from API
  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        const response = await fetchSessionHistory();
        console.log("📦 Delivery History Api result: ", response);

        const list = response?.data || response || [];
        setHistory(list);

        // ✅ Create Location -> Buildings dictionary
        const dict = {};
        list.forEach((item) => {
          const loc = item.building_location || "Unknown";
          const bld = item.building_name || "Unknown";

          if (!dict[loc]) dict[loc] = [];
          if (!dict[loc].includes(bld)) dict[loc].push(bld);
        });

        setLocationDict(dict);

        // ✅ Apply saved filters immediately after load
        const saved =
          JSON.parse(localStorage.getItem("deliveryHistoryFilters")) || {};
        let filtered = [...list];

        if (saved?.selectedLocation && saved.selectedLocation !== "All") {
          filtered = filtered.filter(
            (item) => item.building_location === saved.selectedLocation,
          );
        }

        if (saved?.selectedBuilding && saved.selectedBuilding !== "All") {
          filtered = filtered.filter(
            (item) => item.building_name === saved.selectedBuilding,
          );
        }

        setFilteredHistory(filtered);
      } catch (error) {
        console.error("❌ Failed to load Delivery History", error);
        setHistory([]);
        setFilteredHistory([]);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, []);

  return (
    <div className="min-h-screen bg-white text-white">
      {/* Header */}
      <HamburgerMenuHeader title={t("deliveryHistory")} />

      {/* FILTER SCREEN */}
      {showFilterScreen && (
        <div className="fixed left-0 top-[65px] w-full h-[calc(100%-65px)] bg-white flex flex-col z-50">
          {/* Header */}
          <div className="flex justify-between items-center px-5 py-2 shadow-md">
            <h2 className="text-xl font-semibold text-black">
              {t("advanceFilters")}
            </h2>
            <button
              onClick={() => setShowFilterScreen(false)}
              className="px-3 py-2"
            >
              <img src={cross} alt="X" className="w-8 h-8" />
            </button>
          </div>

          <div className="flex flex-1">
            {/* LEFT TABS */}
            <div className="w-1/3 bg-gray-100 border-r">
              <div
                className={`px-4 py-3 cursor-pointer border-l-4 ${
                  activeTab === "Locations"
                    ? "border-blue-600 bg-white font-medium text-black"
                    : "border-transparent text-gray-600"
                }`}
                onClick={() => setActiveTab("Locations")}
              >
                {t("locations")}
              </div>

              <div
                className={`px-4 py-3 cursor-pointer border-l-4 ${
                  activeTab === "Buildings"
                    ? "border-blue-600 bg-white font-medium text-black"
                    : "border-transparent text-gray-600"
                }`}
                onClick={() => setActiveTab("Buildings")}
              >
                {t("buildings")}
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="w-2/3 px-6 py-4 overflow-y-auto text-black">
              {/* Locations */}
              {activeTab === "Locations" && (
                <>
                  {/* ALL */}
                  <label className="flex items-center gap-3 py-2 cursor-pointer">
                    <input
                      type="radio"
                      name="location"
                      className="w-5 h-5"
                      onChange={() => {
                        setSelectedLocation("All");
                        setSelectedBuilding(null);
                      }}
                      checked={selectedLocation === "All"}
                    />
                    <span>{t("all")}</span>
                  </label>

                  {/* Dynamic Locations */}
                  {Object.keys(locationDict).map((loc) => (
                    <label
                      key={loc}
                      className="flex items-center gap-3 py-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="location"
                        className="w-5 h-5"
                        onChange={() => {
                          setSelectedLocation(loc);
                          setSelectedBuilding(null);
                        }}
                        checked={selectedLocation === loc}
                      />
                      <span>{loc}</span>
                    </label>
                  ))}
                </>
              )}

              {/* Buildings */}
              {activeTab === "Buildings" &&
                (selectedLocation && selectedLocation !== "All" ? (
                  <>
                    {/* ALL */}
                    <label className="flex items-center gap-3 py-2 cursor-pointer">
                      <input
                        type="radio"
                        name="building"
                        className="w-5 h-5"
                        onChange={() => setSelectedBuilding("All")}
                        checked={selectedBuilding === "All"}
                      />
                      <span>{t("all")}</span>
                    </label>

                    {/* Dynamic Buildings */}
                    {locationDict[selectedLocation]?.map((bld) => (
                      <label
                        key={bld}
                        className="flex items-center gap-3 py-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="building"
                          className="w-5 h-5"
                          onChange={() => setSelectedBuilding(bld)}
                          checked={selectedBuilding === bld}
                        />
                        <span>{bld}</span>
                      </label>
                    ))}
                  </>
                ) : (
                  <p className="text-gray-500">
                    {t("pleaseSelectALocationFirst")}
                  </p>
                ))}
            </div>
          </div>

          {/* APPLY BUTTON */}
          <div className="px-6 py-4 flex justify-end">
            <button
              className="bg-green-600 w-full py-3 rounded-lg text-white font-semibold max-w-[200px]"
              onClick={() => {
                let filtered = history;

                // Location filter
                if (selectedLocation && selectedLocation !== "All") {
                  filtered = filtered.filter(
                    (item) => item.building_location === selectedLocation,
                  );
                }

                // Building filter
                if (selectedBuilding && selectedBuilding !== "All") {
                  filtered = filtered.filter(
                    (item) => item.building_name === selectedBuilding,
                  );
                }

                setFilteredHistory(filtered);
                setShowFilterScreen(false);
              }}
            >
              {t("applyFilter")}
            </button>
          </div>
        </div>
      )}

      {/* Content container */}
      <div className="bg-white rounded-t-2xl px-4 py-5 max-w-[1350px] mx-auto">
        {/* Filter buttons */}
        <div className="flex justify-end mb-4 gap-3">
          {/* Clear Filter */}
          {isFilterApplied && (
            <button
              onClick={() => {
                setSelectedLocation("All");
                setSelectedBuilding(null);
                setFilteredHistory(history);
                // localStorage.removeItem("deliveryHistoryFilters");
              }}
              className="text-sm font-semibold bg-white text-red-600 px-4 py-2 rounded-xl border border-[#DFDFDF]"
            >
              {t("clearFilter")}
            </button>
          )}

          {/* Apply Filter */}
          <button
            onClick={() => setShowFilterScreen(true)}
            className="text-sm font-semibold border border-[#DFDFDF] px-4 py-2 rounded-xl text-black"
          >
            {t("applyFilter")}
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center text-gray-500 py-10">
            {t("loading")}...
          </div>
        ) : (filteredHistory || history)?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(filteredHistory || history).map((item, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-2xl px-6 py-5 shadow-sm flex justify-between items-start"
              >
                {/* LEFT SIDE CONTENT */}
                <div>
                  <p className="text-[22px] font-semibold text-[#2699FF]">
                    {item.total_no_of_delivery} {t("deliveries")}
                  </p>

                  <div className="flex gap-2 mt-5">
                    <p className="text-gray-400 font-medium text-[18px]">
                      {t("locations")}:
                    </p>
                    <p className="text-gray-900 font-semibold text-[20px]">
                      {item.building_location}
                    </p>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <p className="text-gray-400 font-medium text-[18px]">
                      {t("buildings")}:
                    </p>
                    <p className="text-gray-900 font-semibold text-[20px]">
                      {item.building_name}
                    </p>
                  </div>

                  <p className="text-gray-400 text-[16px] mt-5">
                    {item.finished_at}
                  </p>
                </div>

                {/* RIGHT SIDE STATUS */}
                <div
                  className={`text-[16px] font-semibold px-6 py-2 rounded-full ${
                    item.status === "completed"
                      ? "bg-green-100 text-[#14AE5C]"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {item.status === "completed" ? t("completed") : t("pending")}
                </div>
              </div>
            ))}
          </div>
        ) : filteredHistory !== null ? (
          // ✅ FILTER APPLIED BUT NO MATCH
          <div className="flex flex-col items-center justify-center py-16">
            <img
              src={noDeliveryHistory}
              alt="No matching history"
              className="w-60 h-60 object-contain"
            />
            <p className="text-center text-gray-600 font-medium text-lg mt-4">
              {t("noMatchingDeliveryRecords")}
            </p>
          </div>
        ) : (
          // ✅ NO HISTORY AT ALL
          <div className="flex flex-col items-center justify-center py-16">
            <img
              src={noDeliveryHistory}
              alt="No delivery history"
              className="w-60 h-60 object-contain"
            />
            <p className="text-center text-gray-600 font-medium text-lg mt-4">
              {t("noDeliveryHistoryRightNow")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryHistory;
